import {
  useActionBarSpace,
  useFixedBarBottomInset,
} from "features/onboarding/common/PhoneActionBar";
import { ReactNode, useRef } from "react";
import styled from "styled-components";
import {
  ONBOARDING_PHONE_GUTTER,
  reveal,
  revealFade,
  stepDescription,
  stepTitle,
} from "features/onboarding/common/onboardingUi";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens";

export const PHONE = 560;

interface WalkthroughLayoutProps {
  title: string;
  subtitle: string;
  /** One quiet line: mode · exchange · change. */
  meta?: ReactNode;
  /** The steps timeline (and anything that belongs with it). */
  story: ReactNode;
  /** The example panel. */
  panel: ReactNode;
  /** The choice: the primary button, then one line of alternatives. */
  actions: ReactNode;
  /** A separate, quieter path under the choice (e.g. "use my exchange"). */
  footer?: ReactNode;
}

/**
 * New look: one shell for every method's "how it works" step. Header (title,
 * one line of subtitle, a quiet meta line); then the story on the left and the
 * example on the right; the CTAs close the story. On phones the example comes
 * first and the choice stays in a bar at the bottom of the screen, while the
 * footer path ends the page.
 */
const WalkthroughLayout = ({
  title,
  subtitle,
  meta,
  story,
  panel,
  actions,
  footer,
}: WalkthroughLayoutProps) => {
  const barRef = useRef<HTMLDivElement>(null);
  // Phones: the bar is fixed, so the page keeps room for it at the bottom.
  useActionBarSpace(barRef);
  const bottomInset = useFixedBarBottomInset();
  return (
    <Layout>
      <Header>
        <Title>{title}</Title>
        <Subtitle>{subtitle}</Subtitle>
        {meta ? <Meta>{meta}</Meta> : null}
      </Header>
      <Body>
        <Story>
          {story}
          <Cta>
            <Actions
              ref={barRef}
              data-onboarding-bar=""
              $bottomInset={bottomInset}
            >
              {actions}
            </Actions>
            {footer}
          </Cta>
        </Story>
        <Visual>{panel}</Visual>
      </Body>
    </Layout>
  );
};

export default WalkthroughLayout;

const Layout = styled.div`
  width: 100%;
  max-width: 1040px;
  margin: 0 auto;
  box-sizing: border-box;
  ${reveal(1)};
  /* Phones: a fade without the rise. The action bar is fixed here, and a
     transform on this ancestor would hold it inside the page until the
     motion ended (the bar "popped" to the bottom a moment later). */
  @media (max-width: ${PHONE}px) {
    ${revealFade(1)};
  }
  /* Phones: edge to edge with its own gutter, whatever padding the step's
     wrapper has (some onboarding steps have none). Not 100vw: that counts a
     desktop browser's scrollbar and shifts the column. */
  @media (max-width: ${PHONE}px) {
    width: auto;
    max-width: none;
    margin: 0 calc(-1 * var(--onboarding-step-padding, 0px));
    padding: 0 ${ONBOARDING_PHONE_GUTTER}px;
  }
`;
const Header = styled.header`
  max-width: 640px;
  margin: 0 auto 24px;
  text-align: center;
  @media (max-width: ${PHONE}px) {
    margin-bottom: 16px;
    text-align: left;
  }
`;
const Title = styled.h1`
  ${stepTitle};
  margin: 0 0 4px;
  @media (max-width: ${PHONE}px) {
    /* 8px under the journey line, as the method steps' eyebrow. */
    margin-top: 8px;
    font-size: 21px;
    line-height: 28px;
  }
`;
const Subtitle = styled.p`
  ${stepDescription};
  margin: 0;
  /* Phones: the steps under the example explain it; the title is enough. */
  @media (max-width: ${PHONE}px) {
    display: none;
  }
`;
const Meta = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0 8px;
  margin-top: 8px;
  @media (max-width: ${PHONE}px) {
    justify-content: flex-start;
    margin-top: 6px;
  }
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  .ant-btn,
  button {
    height: auto;
    padding: 0;
    font-size: 12px;
    line-height: 16px;
  }
`;
const Body = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 320px) minmax(0, 1fr);
  grid-template-areas: "story visual";
  gap: 32px;
  align-items: start;
  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas: "visual" "story";
    gap: 24px;
  }
  @media (max-width: ${PHONE}px) {
    gap: 16px;
  }
`;
const Story = styled.section`
  grid-area: story;
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-width: 0;
`;
const Visual = styled.div`
  grid-area: visual;
  min-width: 0;
`;
const Cta = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  @media (max-width: ${PHONE}px) {
    /* The choice is in the bottom bar; the footer path follows the steps. */
    > p:first-of-type {
      margin-top: 0;
    }
  }
`;
const Actions = styled.div<{ $bottomInset: string }>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  > .ant-btn {
    width: 100%;
  }
  @media (max-width: ${PHONE}px) {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 20;
    padding: 12px ${ONBOARDING_PHONE_GUTTER}px
      calc(8px + ${(p) => p.$bottomInset});
    border-top: 1px solid ${(p) => p.theme.colors.cardBorderColor};
    background: ${(p) => p.theme.colors.cardBgColor};
    gap: 4px;
  }
`;
